import React, { useState } from 'react';
import { Edit, Quote } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './TestimonialsPage.scss';

interface Testimonial {
  id: number;
  author_name: string;
  company_name: string;
  content: string;
  created_at: string;
}

// Mock testimonials data - in production, fetch from API
const mockTestimonials: Testimonial[] = [
  {
    id: 1,
    author_name: 'Dainius Reklaitis',
    company_name: 'UAB "Green"',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo. Proin sodales pulvinar tempor. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nam fermentum...',
    created_at: '2024-01-15'
  },
  {
    id: 2,
    author_name: 'Irmantas Sadevičius',
    company_name: 'UAB "Menerta"',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo.',
    created_at: '2024-01-10'
  },
  {
    id: 3,
    author_name: 'Dainius Reklaitis',
    company_name: 'UAB "Green"',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo. Proin sodales pulvinar tempor. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nam fermentum...',
    created_at: '2024-01-08'
  },
  {
    id: 4,
    author_name: 'Irmantas Sadevičius',
    company_name: 'UAB "Menerta"',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo.',
    created_at: '2024-01-05'
  },
  {
    id: 5,
    author_name: 'Irmantas Sadevičius',
    company_name: 'UAB "Menerta"',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo.',
    created_at: '2024-01-03'
  },
  {
    id: 6,
    author_name: 'Dainius Reklaitis',
    company_name: 'UAB "Green"',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo. Proin sodales pulvinar tempor. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nam fermentum...',
    created_at: '2024-01-01'
  },
  {
    id: 7,
    author_name: 'Dainius Reklaitis',
    company_name: 'UAB "Green"',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo. Proin sodales pulvinar tempor. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nam fermentum...',
    created_at: '2023-12-28'
  },
  {
    id: 8,
    author_name: 'Irmantas Sadevičius',
    company_name: 'UAB "Menerta"',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo.',
    created_at: '2023-12-25'
  },
];

const TestimonialsPage: React.FC = () => {
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState<Testimonial[]>(mockTestimonials);
  const [showModal, setShowModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [newReview, setNewReview] = useState({ content: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleWriteReview = () => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    setShowModal(true);
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newReview.content.trim()) return;

    setSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      const review: Testimonial = {
        id: testimonials.length + 1,
        author_name: user.company_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
        company_name: user.company_name || '',
        content: newReview.content,
        created_at: new Date().toISOString().split('T')[0]
      };
      
      setTestimonials([review, ...testimonials]);
      setNewReview({ content: '' });
      setShowModal(false);
      setSubmitting(false);
    }, 500);
  };

  return (
    <div className="testimonials-page">
      {/* Hero Section */}
      <section className="testimonials-hero">
        <div className="hero-content">
          <h1>Klientų atsiliepimai</h1>
          <p>Sužinokite, ką mūsų klientai sako apie mus</p>
          <button className="write-review-btn" onClick={handleWriteReview}>
            <Edit size={18} />
            RAŠYTI ATSILIEPIMĄ
          </button>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="testimonials-section">
        <div className="testimonials-grid">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="testimonial-card">
              <div className="quote-icon top">
                <Quote size={24} />
              </div>
              <div className="card-header">
                <h3>{testimonial.author_name}</h3>
                <span className="company">{testimonial.company_name}</span>
              </div>
              <p className="content">{testimonial.content}</p>
              <div className="quote-icon bottom">
                <Quote size={24} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="modal-overlay" onClick={() => setShowLoginPrompt(false)}>
          <div className="modal-content login-prompt" onClick={e => e.stopPropagation()}>
            <h2>Prisijunkite</h2>
            <p>Norėdami palikti atsiliepimą, turite būti prisijungę.</p>
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowLoginPrompt(false)}
              >
                Atšaukti
              </button>
              <a href="/login" className="btn-primary">
                Prisijungti
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Write Review Modal */}
      {showModal && user && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content review-modal" onClick={e => e.stopPropagation()}>
            <h2>Rašyti atsiliepimą</h2>
            <form onSubmit={handleSubmitReview}>
              <div className="form-group">
                <label>Jūsų atsiliepimas</label>
                <textarea
                  value={newReview.content}
                  onChange={(e) => setNewReview({ content: e.target.value })}
                  placeholder="Pasidalinkite savo patirtimi..."
                  rows={6}
                  required
                />
              </div>
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => setShowModal(false)}
                >
                  Atšaukti
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={submitting || !newReview.content.trim()}
                >
                  {submitting ? 'Siunčiama...' : 'Paskelbti'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsPage;

